import SettingsLayout from 'modules/profile/components/settings-layout'
import { settingsMenuLocoKeys } from 'modules/profile/constants/settings-menu-loco-keys'
import { PaymentGatewaysLoco } from 'modules/profile/payment-gateways/constants/payment-gateways-loco'
import { PaymentGatewaysEnum } from 'modules/profile/payment-gateways/enums/payment-gateways'
import { CashOnDelivery } from 'modules/profile/payment-gateways/integrations/cash-on-delivery'
import { Flutterwave } from 'modules/profile/payment-gateways/integrations/flutterwave'
import { Gocardless } from 'modules/profile/payment-gateways/integrations/gocardless'
import { Mercadopago } from 'modules/profile/payment-gateways/integrations/mercadopago'
import { Mollie } from 'modules/profile/payment-gateways/integrations/mollie'
import { PaypalRest } from 'modules/profile/payment-gateways/integrations/paypal-rest'
import { usePaypalIntegrationType } from 'modules/profile/payment-gateways/integrations/paypal-rest/hooks/use-paypal-integration-type'
import { PaypalSoap } from 'modules/profile/payment-gateways/integrations/paypal-soap'
import { Paystack } from 'modules/profile/payment-gateways/integrations/paystack'
import { Razorpay } from 'modules/profile/payment-gateways/integrations/razorpay'
import { SecureAndPay } from 'modules/profile/payment-gateways/integrations/secure-and-pay'
import { Stripe } from 'modules/profile/payment-gateways/integrations/stripe'
import { Xendit } from 'modules/profile/payment-gateways/integrations/xendit'
import { NextPage } from 'next'
import React, { useMemo } from 'react'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { getBaseServerSideProps } from 'shared/utils/get-base-server-side-props'

const PaymentGateways: NextPage = () => {
  const { t } = useLocoTranslation()

  const getPaymentIntegration = (payment: PaymentGatewaysEnum): JSX.Element => {
    switch (payment) {
      case PaymentGatewaysEnum.CashOnDelivery:
        return <CashOnDelivery />
      case PaymentGatewaysEnum.Flutterwave:
        return <Flutterwave />
      case PaymentGatewaysEnum.Gocardless:
        return <Gocardless />
      case PaymentGatewaysEnum.MercadoPago:
        return <Mercadopago />
      case PaymentGatewaysEnum.Mollie:
        return <Mollie />
      case PaymentGatewaysEnum.PaypalRest:
        return <PaypalRest />
      case PaymentGatewaysEnum.PaypalSoap:
        return <PaypalSoap />
      case PaymentGatewaysEnum.Paystack:
        return <Paystack />
      case PaymentGatewaysEnum.Razorpay:
        return <Razorpay />
      case PaymentGatewaysEnum.SecureAndPay:
        return <SecureAndPay />
      case PaymentGatewaysEnum.Stripe:
        return <Stripe />
      case PaymentGatewaysEnum.Xendit:
        return <Xendit />
    }
  }

  const { shouldShowRest, shouldShowSoap } = usePaypalIntegrationType()

  const paymentGateways = useMemo(() => {
    if (shouldShowRest && shouldShowSoap) {
      return Object.values(PaymentGatewaysEnum)
    }

    if (shouldShowRest) {
      return Object.values(PaymentGatewaysEnum).filter(pg => pg !== PaymentGatewaysEnum.PaypalSoap)
    }

    if (shouldShowSoap) {
      return Object.values(PaymentGatewaysEnum).filter(pg => pg !== PaymentGatewaysEnum.PaypalRest)
    }

    return Object.values(PaymentGatewaysEnum).filter(
      pg => pg !== PaymentGatewaysEnum.PaypalRest && pg !== PaymentGatewaysEnum.PaypalSoap,
    )
  }, [shouldShowRest, shouldShowSoap])

  return (
    <SettingsLayout
      titles={[{ caption: t(settingsMenuLocoKeys.payment_gateways), isStatic: true }]}
      needSave={false}
    >
      <div className="bg-background-secondary flex h-fit w-full justify-center overflow-x-hidden rounded-md lg:justify-start lg:px-8 lg:py-3">
        <div className={'flex w-full flex-col justify-center [&>*]:flex-1'}>
          {paymentGateways.map((el, idx) => (
            <React.Fragment key={el}>
              <div className={'flex w-full flex-col px-6 py-12 lg:px-0 lg:py-8'}>
                {getPaymentIntegration(el)}
              </div>
              {idx !== paymentGateways.length - 1 && <hr className={'text-text-secondary/30'} />}
            </React.Fragment>
          ))}
        </div>
      </div>
    </SettingsLayout>
  )
}

export const getServerSideProps = getBaseServerSideProps(PaymentGatewaysLoco, 'global.settings')

export default PaymentGateways
